import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Badge, Button, Spinner } from 'react-bootstrap';
import {
    DatabaseFill,
    Eye,
    PencilFill,
    Trash,
    Check2,
    X,
    PlusCircle,
} from 'react-bootstrap-icons';

import apiClient, { API_ENDPOINTS } from '../api/config';
import { listRequirementGenerations } from '../api/requirements';
import { RequirementGenerationJob } from '../types/requirements';
import { TestDataset, TestDatasetDetail } from '../types/testData';
import TestDatasetPreviewModal from '../components/testdata/TestDatasetPreviewModal';
import {
    filenameFromContentDisposition,
    triggerDownloadFromBlob,
} from '../utils/download';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const ACTIVE_SYNTHETIC_JOB_STATUSES = new Set(['queued', 'pending', 'running', 'retrying']);

function isActiveSyntheticJobStatus(status?: string | null): boolean {
    return ACTIVE_SYNTHETIC_JOB_STATUSES.has((status || '').toLowerCase());
}

function formatDate(iso: string): string {
    try {
        return new Date(iso.includes('Z') || iso.includes('+') ? iso : iso + 'Z').toLocaleString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    } catch {
        return iso;
    }
}

// ---------------------------------------------------------------------------
// Inline rename component
// ---------------------------------------------------------------------------

interface InlineRenameProps {
    initial: string;
    onSave: (name: string) => void;
    onCancel: () => void;
}

function InlineRename({ initial, onSave, onCancel }: InlineRenameProps): React.ReactElement {
    const [value, setValue] = useState(initial);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
    }, []);

    function handleKeyDown(e: React.KeyboardEvent): void {
        if (e.key === 'Enter') onSave(value.trim());
        if (e.key === 'Escape') onCancel();
    }

    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <input
                ref={inputRef}
                value={value}
                onChange={e => setValue(e.target.value)}
                onKeyDown={handleKeyDown}
                style={{
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    border: '1px solid #2563eb',
                    borderRadius: 6,
                    padding: '2px 8px',
                    outline: 'none',
                    color: '#172033',
                    width: 200,
                }}
            />
            <button
                type="button"
                onClick={() => onSave(value.trim())}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#16a34a', padding: 2 }}
                title="Save"
            >
                <Check2 size={16} />
            </button>
            <button
                type="button"
                onClick={onCancel}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: 2 }}
                title="Cancel"
            >
                <X size={16} />
            </button>
        </span>
    );
}

// ---------------------------------------------------------------------------
// Dataset card
// ---------------------------------------------------------------------------

const MAX_COLUMN_CHIPS = 5;

interface DatasetCardProps {
    dataset: TestDataset;
    onPreview: (id: string) => void;
    onRename: (id: string, name: string) => void;
    onDelete: (id: string) => void;
}

function DatasetCard({ dataset, onPreview, onRename, onDelete }: DatasetCardProps): React.ReactElement {
    const [editing, setEditing] = useState(false);

    const visibleCols = dataset.columns.slice(0, MAX_COLUMN_CHIPS);
    const extraCols = dataset.columns.length - MAX_COLUMN_CHIPS;

    return (
        <div style={{
            background: '#ffffff',
            border: editing ? '1px solid #2563eb' : '1px solid #e8ecf0',
            borderRadius: 12,
            padding: '18px 20px',
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
            boxShadow: editing ? '0 4px 12px rgba(37, 99, 235, 0.1)' : 'none',
            transition: 'all 0.2s ease'
        }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                    {editing ? (
                        <InlineRename
                            initial={dataset.name}
                            onSave={name => { setEditing(false); if (name) onRename(dataset.id, name); }}
                            onCancel={() => setEditing(false)}
                        />
                    ) : (
                        <span style={{ fontWeight: 700, fontSize: '0.95rem', color: '#172033', wordBreak: 'break-word' }}>
                            {dataset.name}
                        </span>
                    )}
                    {!editing && (
                        <div style={{ fontSize: '0.78rem', color: '#9ca3af', marginTop: 2 }}>
                            {formatDate(dataset.created_at)}
                        </div>
                    )}
                </div>
                {!editing && (
                    <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                        <Badge
                            bg={dataset.source === 'generated' || dataset.source === 'synthetic_generation' ? 'primary' : 'secondary'}
                            style={{ fontSize: '0.72rem', fontWeight: 500 }}
                        >
                            {dataset.source}
                        </Badge>
                        <Badge bg="light" text="dark" style={{ fontSize: '0.72rem', fontWeight: 500, border: '1px solid #e8ecf0' }}>
                            {dataset.row_count} row{dataset.row_count !== 1 ? 's' : ''}
                        </Badge>
                    </div>
                )}
            </div>

            {/* Column chips */}
            {dataset.columns.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, opacity: editing ? 0.5 : 1 }}>
                    {visibleCols.map(col => (
                        <span key={col} style={{
                            background: '#f0f4ff',
                            color: '#3b5bdb',
                            borderRadius: 6,
                            padding: '2px 8px',
                            fontSize: '0.75rem',
                            fontWeight: 500,
                        }}>
                            {col}
                        </span>
                    ))}
                    {extraCols > 0 && (
                        <span style={{
                            background: '#f3f4f6',
                            color: '#6b7280',
                            borderRadius: 6,
                            padding: '2px 8px',
                            fontSize: '0.75rem',
                        }}>
                            +{extraCols} more
                        </span>
                    )}
                </div>
            )}

            {/* Linked test cases */}
            {(() => {
                const selected = dataset.linked_test_case_count ?? dataset.linked_test_case_ids.length;
                const generated = dataset.generated_test_case_count ?? selected;
                if (selected <= 0) return null;
                const label = dataset.source === 'synthetic_generation'
                    ? `Linked to ${generated} of ${selected} test case${selected !== 1 ? 's' : ''}`
                    : `Linked to ${selected} test case${selected !== 1 ? 's' : ''}`;
                return (
                    <div style={{ fontSize: '0.8rem', color: '#059669', opacity: editing ? 0.5 : 1 }}>
                        {label}
                    </div>
                );
            })()}

            {/* Actions */}
            <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => onPreview(dataset.id)}
                    disabled={editing}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.8rem' }}
                >
                    <Eye size={13} /> Preview
                </Button>
                <Button
                    variant={editing ? "primary" : "outline-secondary"}
                    size="sm"
                    onClick={() => setEditing(true)}
                    disabled={editing}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.8rem' }}
                >
                    <PencilFill size={12} /> Rename
                </Button>
                <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => onDelete(dataset.id)}
                    disabled={editing}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.8rem', marginLeft: 'auto' }}
                >
                    <Trash size={13} />
                </Button>
            </div>
        </div>
    );
}

interface PendingDatasetCardProps {
    job: RequirementGenerationJob;
}

function PendingDatasetCard({ job }: PendingDatasetCardProps): React.ReactElement {
    const normalizedStatus = (job.status || 'pending').toLowerCase();

    return (
        <div style={{
            background: '#ffffff',
            border: '1px dashed #bfdbfe',
            borderRadius: 12,
            padding: '18px 20px',
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
            boxShadow: '0 4px 12px rgba(37, 99, 235, 0.06)'
        }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ fontWeight: 700, fontSize: '0.95rem', color: '#172033' }}>
                        Synthetic dataset job #{job.id}
                    </span>
                    <div style={{ fontSize: '0.78rem', color: '#9ca3af', marginTop: 2 }}>
                        Started {formatDate(job.created_at)}
                    </div>
                </div>
                <Badge
                    bg="primary"
                    style={{ fontSize: '0.72rem', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 6 }}
                >
                    <Spinner animation="border" size="sm" style={{ width: 10, height: 10 }} />
                    {normalizedStatus}
                </Badge>
            </div>
            <div style={{ fontSize: '0.82rem', color: '#4b5563', lineHeight: 1.55 }}>
                Dataset generation is in progress. This card will be replaced by the completed dataset after refresh.
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <span style={{
                    background: '#eff6ff',
                    color: '#2563eb',
                    borderRadius: 6,
                    padding: '2px 8px',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                }}>
                    Synthetic data
                </span>
                {typeof job.progress === 'number' && (
                    <span style={{
                        background: '#f3f4f6',
                        color: '#6b7280',
                        borderRadius: 6,
                        padding: '2px 8px',
                        fontSize: '0.75rem',
                    }}>
                        {job.progress}% complete
                    </span>
                )}
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function TestDataPage(): React.ReactElement {
    const { projectId } = useParams<{ projectId: string }>();
    const navigate = useNavigate();

    const [datasets, setDatasets] = useState<TestDataset[]>([]);
    const [activeSyntheticJobs, setActiveSyntheticJobs] = useState<RequirementGenerationJob[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const hadActiveSyntheticJobRef = useRef(false);

    // Preview modal
    const [previewDataset, setPreviewDataset] = useState<TestDatasetDetail | null>(null);
    const [previewLoading, setPreviewLoading] = useState(false);

    // Delete confirmation
    const [deletingId, setDeletingId] = useState<string | null>(null);

    // -----------------------------------------------------------------------
    // Data fetching
    // -----------------------------------------------------------------------

    const fetchDatasets = useCallback(async (showLoading = true) => {
        if (!projectId) return;
        if (showLoading) {
            setLoading(true);
        }
        setError(null);
        try {
            const [datasetRes, jobs] = await Promise.all([
                apiClient.get<TestDataset[]>(API_ENDPOINTS.TEST_DATA_LIST(projectId)),
                listRequirementGenerations(projectId),
            ]);
            setDatasets(datasetRes.data);
            setActiveSyntheticJobs(
                jobs.filter(job =>
                    (job.generation_kind || '').toLowerCase() === 'synthetic_data'
                    && isActiveSyntheticJobStatus(job.status)
                )
            );
        } catch (err: any) {
            setError(err?.response?.data?.detail ?? 'Failed to load test datasets.');
        } finally {
            setLoading(false);
        }
    }, [projectId]);

    useEffect(() => { fetchDatasets(); }, [fetchDatasets]);

    useEffect(() => {
        const hasActiveSyntheticJob = activeSyntheticJobs.length > 0;

        // The final polling response can contain a completed job but a stale dataset list.
        // Reload once more after the job settles so the completed card appears immediately.
        if (hadActiveSyntheticJobRef.current && !hasActiveSyntheticJob) {
            void fetchDatasets(false);
        }
        hadActiveSyntheticJobRef.current = hasActiveSyntheticJob;

        if (!hasActiveSyntheticJob) return;

        const interval = window.setInterval(() => {
            fetchDatasets(false);
        }, 5000);
        return () => window.clearInterval(interval);
    }, [activeSyntheticJobs.length, fetchDatasets]);

    // -----------------------------------------------------------------------
    // Actions
    // -----------------------------------------------------------------------

    async function handlePreview(datasetId: string): Promise<void> {
        if (!projectId) return;
        setPreviewLoading(true);
        try {
            const res = await apiClient.get<TestDatasetDetail>(API_ENDPOINTS.TEST_DATA_DETAIL(projectId, datasetId));
            setPreviewDataset(res.data);
        } catch {
            // silently ignore — preview just won't open
        } finally {
            setPreviewLoading(false);
        }
    }

    function handleDownload(datasetId: string): void {
        if (!projectId) return;
        const url = API_ENDPOINTS.TEST_DATA_DOWNLOAD(projectId, datasetId);

        apiClient.get(url, { responseType: 'blob' })
            .then(response => {
                const ds = datasets.find(d => d.id === datasetId);
                const fallback = ds ? `${ds.name.replace(/\s+/g, '_')}.csv` : 'dataset.csv';
                const filename = filenameFromContentDisposition(
                    response.headers?.['content-disposition'],
                    fallback,
                );
                triggerDownloadFromBlob(response.data, filename);
            });
    }

    async function handleRename(datasetId: string, name: string): Promise<void> {
        if (!projectId) return;
        try {
            const res = await apiClient.patch<TestDataset>(
                API_ENDPOINTS.TEST_DATA_RENAME(projectId, datasetId),
                { name },
            );
            setDatasets(prev => prev.map(d => d.id === datasetId ? res.data : d));
        } catch {
            // Silently fail — user still sees old name
        }
    }

    async function handleDelete(datasetId: string): Promise<void> {
        if (!projectId) return;
        if (!window.confirm('Delete this dataset? This cannot be undone.')) return;
        setDeletingId(datasetId);
        try {
            await apiClient.delete(API_ENDPOINTS.TEST_DATA_DELETE(projectId, datasetId));
            setDatasets(prev => prev.filter(d => d.id !== datasetId));
        } catch {
            // Silently fail
        } finally {
            setDeletingId(null);
        }
    }

    // -----------------------------------------------------------------------
    // Render
    // -----------------------------------------------------------------------

    return (
        <div style={{ padding: '28px 24px', maxWidth: 1100 }}>
            {/* Page header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{
                        width: 36,
                        height: 36,
                        borderRadius: 10,
                        background: '#eff6ff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}>
                        <DatabaseFill size={18} color="#2563eb" />
                    </div>
                    <div>
                        <h1 style={{ fontSize: '1.15rem', fontWeight: 700, color: '#172033', margin: 0 }}>
                            Generated Datasets
                        </h1>
                        <p style={{ fontSize: '0.82rem', color: '#6b7280', margin: 0 }}>
                            Structured datasets for your test cases
                        </p>
                    </div>
                </div>
                <Button
                    variant="primary"
                    size="sm"
                    onClick={() => navigate(`/projects/${projectId}?tab=synthetic-data-gen`)}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                >
                    <PlusCircle size={14} />
                    Back to Synthetic Data
                </Button>
            </div>

            {/* States */}
            {loading && (
                <div style={{ textAlign: 'center', padding: '60px 0', color: '#6b7280' }}>
                    <Spinner animation="border" size="sm" style={{ marginRight: 8 }} />
                    Loading datasets…
                </div>
            )}

            {!loading && error && (
                <div style={{
                    background: '#fef2f2',
                    border: '1px solid #fecaca',
                    borderRadius: 10,
                    padding: '16px 20px',
                    color: '#dc2626',
                    fontSize: '0.88rem',
                }}>
                    {error}
                </div>
            )}

            {!loading && !error && datasets.length === 0 && activeSyntheticJobs.length === 0 && (
                <div style={{
                    background: '#ffffff',
                    border: '1px solid #e8ecf0',
                    borderRadius: 16,
                    padding: '56px 32px',
                    textAlign: 'center',
                    maxWidth: 520,
                    margin: '0 auto',
                }}>
                    <div style={{
                        width: 56,
                        height: 56,
                        borderRadius: 14,
                        background: '#f0f4ff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 18px',
                    }}>
                        <DatabaseFill size={24} color="#2563eb" />
                    </div>
                    <h2 style={{ fontSize: '1.05rem', fontWeight: 700, color: '#172033', margin: '0 0 8px' }}>
                        No datasets yet
                    </h2>
                    <p style={{ color: '#6b7280', fontSize: '0.875rem', lineHeight: 1.6, margin: 0 }}>
                        Datasets are created when you run synthetic data generation against your test cases.
                        Click <strong>Back to Synthetic Data</strong> to get started.
                    </p>
                </div>
            )}

            {!loading && !error && (datasets.length > 0 || activeSyntheticJobs.length > 0) && (
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                    gap: 16,
                }}>
                    {activeSyntheticJobs.map(job => (
                        <PendingDatasetCard key={`synthetic-job-${job.id}`} job={job} />
                    ))}
                    {datasets.map(dataset => (
                        <div key={dataset.id} style={{ opacity: deletingId === dataset.id ? 0.5 : 1, pointerEvents: deletingId === dataset.id ? 'none' : undefined }}>
                            <DatasetCard
                                dataset={dataset}
                                onPreview={handlePreview}
                                onRename={handleRename}
                                onDelete={handleDelete}
                            />
                        </div>
                    ))}
                </div>
            )}

            {/* Preview modal */}
            {previewLoading && (
                <div style={{
                    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
                }}>
                    <Spinner animation="border" variant="light" />
                </div>
            )}
            {previewDataset && (
                <TestDatasetPreviewModal
                    dataset={previewDataset}
                    defaultView="table"
                    onClose={() => setPreviewDataset(null)}
                    onDownload={id => { handleDownload(id); setPreviewDataset(null); }}
                />
            )}
        </div>
    );
}

export default TestDataPage;
